import { useEffect, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import { pageScrollerOf } from './useScrollSpy';

/** The element a location's hash names, if it names one. */
function hashTarget(hash: string): HTMLElement | null {
  if (hash.length < 2) return null;
  let id: string;
  try {
    id = decodeURIComponent(hash.slice(1));
  } catch {
    return null;
  }
  return document.getElementById(id);
}

/**
 * Where an Edit Brand Workspace page opens (spec:277; web.md §6.1): at the top whenever the page, or
 * the workspace, changes; at the section a hash names (`/workspace/edit/workflow#approvals`, the
 * test-mode link) one frame after it renders, once the cards above it are laid out, as the old page
 * does for `#approvals`. The page's scroll region is found from `anchor`.
 */
export function useEditPageScroll(anchor: RefObject<HTMLElement | null>, page: string, tenantId: string): void {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash.length > 1) {
      const frame = requestAnimationFrame(() => {
        hashTarget(hash)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      });
      return () => cancelAnimationFrame(frame);
    }
    const scroller = pageScrollerOf(anchor.current);
    if (scroller) scroller.scrollTop = 0;
    return undefined;
  }, [anchor, page, tenantId, hash]);
}
